import Label from "@/v2-ui/label"
import Switch from "@/v2-ui/switch"
import { useLicenseSeatCandidatesQuery } from "@/v2-backoffice-client/license/create/license.create.seatCandidates.query"
import LicenseSeatLimitFields from "@/v2-backoffice-client/license/seats/LicenseSeatLimitFields"
import type { LicenseSeatLimitDraft } from "@/v2-backoffice-client/license/seats/license.seats.utils"

export type LicenseCreateSeatLimitProps = {
  customerId: number
  customerName: string
  /** Whether the operator has opened the "named people" step; null draft = everyone. */
  isLimited: boolean
  onIsLimitedChange(isLimited: boolean): void
  draft: LicenseSeatLimitDraft
  onDraftChange(next: LicenseSeatLimitDraft): void
}

// The Grant dialog's optional second step for a seated product (the MCP
// servers), folded into the first: off, the grant covers everyone at the
// customer (the default); on, the operator ticks the people and the grant
// lands the license already limited to them, in one transaction
// (backoffice.Insert_Lic_License with @seatUserIds). The same fields as the
// Seats dialog's Limit Seats, so the two places read alike; the people come
// from getLicenseSeatCandidates because the license does not exist yet.
function LicenseCreateSeatLimit(props: LicenseCreateSeatLimitProps) {
  const {
    customerId,
    customerName,
    isLimited,
    onIsLimitedChange,
    draft,
    onDraftChange,
  } = props

  const { data, loading, error } = useLicenseSeatCandidatesQuery({
    variables: { customerId },
    skip: !isLimited,
  })
  const people = data?.getLicenseSeatCandidates ?? []

  return (
    <div className="space-y-3 rounded-md border border-border px-3 py-2">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label htmlFor="licenseCreateIsLimited" className="text-sm">
            Only named people
          </Label>
          <p className="text-xs text-muted-foreground">
            {isLimited
              ? "Only the people ticked below get this license."
              : `Off: everyone at ${customerName} gets this license. You can limit it to named people later from the row's Seats.`}
          </p>
        </div>
        <Switch
          id="licenseCreateIsLimited"
          data-feature-code="UI_BackofficeLicense_Create_SeatLimit"
          checked={isLimited}
          onCheckedChange={onIsLimitedChange}
        />
      </div>

      {isLimited && (
        <LicenseSeatLimitFields
          people={people}
          isLoading={loading}
          error={error}
          customerName={customerName}
          draft={draft}
          onDraftChange={onDraftChange}
          // A grant from here is always an operator license: the count is
          // the operator's own number and follows the people picked.
          seatsFollowPicked
          idPrefix="licenseCreateSeatLimit"
        />
      )}
    </div>
  )
}

export default LicenseCreateSeatLimit
